import { useState, useEffect } from 'react'
import { Form as FinalForm, Field } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import arrayMutators from 'final-form-arrays'
import { useTranslation } from 'react-i18next'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { Grid, Typography, Tooltip, Button } from '@mui/material'
import Dialog from '../../control/Dialog'
import Textbox from '../../control/Textbox'
import { TextEditorType } from '../../control/textEditor/TextEditor'
import UploadInput from '../input/UploadInput'
import TextEditorInput from '../input/TextEditorInput'
import useContent, { Form, validate } from '../../hooks/backoffice/useContent'
import BackIcon from '@mui/icons-material/ArrowBackIosNewRounded'
import FileIcon from '@mui/icons-material/NoteAddRounded'
import AddIcon from '@mui/icons-material/AddRounded'
import MinusIcon from '@mui/icons-material/RemoveRounded'
import { useSelector } from 'react-redux'
import { RootStateType } from '../../redux/reducer/rootReducer'
import { Box } from "@mui/material";

type ManageContentFormProps = {}

const ManageContentForm = (props: ManageContentFormProps) => {
    const {
        data,
        alertMessageError,
        openDialogError,
        alertMessageOk,
        openDialogBack,
        openDialogSave,
        openDialogOk,
        onClickDeleteFieldArrayItem,
        onClickConfirmSave,
        onClickCancelSave,
        onClickBack,
        onClickForceBack,
        onClickCancelBack,
        onClickConfirmBack,
        onClickOk,
        onSubmitForm,
        callServiceGetById,
    } = useContent()
    const { t } = useTranslation()
    const classes = useStyles()

    const validates = (values: Form) => {
        const validation = validate(values, t) || {}
        return validation
    }

    const [initialContentHtml, setInitialContentHtml] = useState<TextEditorType>()
    const [initialValues, setInitialValues] = useState<Form>({})
    const [titleName, setTitleName] = useState<string>()
    const [contentId, setContentId] = useState<number>(0)
    const { isLoggedIn } = useSelector(({ profile }: RootStateType) => profile)

    useEffect(() => {
        const url = globalThis.location.href
        const splitUrl = url.split('/')
        const id = parseInt(splitUrl[splitUrl.length - 2])
        const title = data.filter((item: ContentType) => item.contentId === id)?.[0]?.contentTypeName
        setTitleName(`${t('BUTTON.EDIT')}${t('LABEL.BACKOFFICE.CONTENT.TITLE')}${title}`)
        setContentId(id)
    }, [data])

    useEffect(() => {
        if (isLoggedIn) {
            const url = globalThis.location.href
            const splitUrl = url.split('/')
            const id = parseInt(splitUrl[splitUrl.length - 2])
            setContentId(id)
            callServiceGetInitial(id)
        }
    }, [isLoggedIn])

    const callServiceGetInitial = async (id: number) => {
        const { result } = await callServiceGetById(id)

        if (result?.data) {
            setInitialValues({
                title: result?.data.title,
                coverImage: result?.data.coverImage ? [result?.data.coverImage] : [],
                additionalImage: result?.data.pictures,
                attachment: result?.data.pdfLinks,
                externalLink:
                    result?.data.externalLinks?.length > 0
                        ? result?.data?.externalLinks?.map((item: any) => ({
                            name: item?.displayText,
                            url: item?.urlPath,
                        }))
                        : [
                            {
                                name: '',
                                url: '',
                            },
                        ],
            })
            setInitialContentHtml({
                htmlTag: result?.data.body,
                value: '',
            })
        }
    }

    return (
        <>
            <FinalForm
                onSubmit={onSubmitForm}
                validate={validates}
                initialValues={initialValues}
                subscription={{ values: true }}
                mutators={{
                    ...arrayMutators,
                }}
            >
                {({ handleSubmit, values: formData, form }) => (
                    <form onSubmit={handleSubmit}>
                        <StyledDivHeader>
                            <Tooltip title={`กลับไปหน้า${t('MENU.CONTENT_MANAGEMENT')}`} placement="top">
                                <Button
                                    onClick={() => {
                                        onClickBack(formData, contentId)
                                    }}
                                    className={classes.backButton}
                                >
                                    <IconBack />
                                </Button>
                            </Tooltip>
                            <StyledTypoHeader variant="h6">{titleName}</StyledTypoHeader>
                        </StyledDivHeader>
                        <StyledDivForm>
                            <Grid container spacing={2} className={classes.container}>
                                <Grid item xs={12}>
                                    <Field
                                        name="title"
                                        label={`หัวข้อ`}
                                        placeholder={`หัวข้อ`}
                                        component={Textbox}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                                {contentId !== 1 && (
                                    <Grid item xs={12}>
                                        <Typography>{t('LABEL.BACKOFFICE.CONTACT.IMAGE')}</Typography>
                                        <Field
                                            name="coverImage"
                                            label={t('LABEL.BACKOFFICE.UPLOAD')}
                                            placeholder={t('LABEL.BACKOFFICE.PLACEHOLDER_UPLOAD')}
                                            component={UploadInput}
                                            maxFile={1}
                                            displayLabelFileList={false}
                                            allowExtension={['image/jpg', 'image/jpeg', 'image/png']}
                                        />
                                    </Grid>
                                )}
                                <Grid item xs={12} >
                                    <Typography>{'เนื้อหา'}</Typography>
                                    <Box className={classes.editorWrapper} >
                                        <Field
                                            name="content"
                                            placeholder={'จัดการเนื้อหา'}
                                            component={TextEditorInput}
                                            initialRichTextEditor={initialContentHtml}
                                        />
                                    </Box>
                                </Grid>
                                {contentId !== 1 && (
                                    <Grid item xs={12}>
                                        <Typography>{'รูปภาพเพิ่มเติม'}</Typography>
                                        <Field
                                            name="additionalImage"
                                            label={t('LABEL.BACKOFFICE.UPLOAD')}
                                            placeholder={t('LABEL.BACKOFFICE.PLACEHOLDER_UPLOAD')}
                                            component={UploadInput}
                                            maxFile={10}
                                            displayLabelFileList={false}
                                            allowExtension={['image/jpg', 'image/jpeg', 'image/png']}
                                        />
                                    </Grid>
                                )}
                                {contentId !== 1 && (
                                    <Grid item xs={12}>
                                        <Typography>{'อัพโหลดไฟล์ PDF เพิ่มเติม'}</Typography>
                                        <Field
                                            name="attachment"
                                            label={'ลากวาง หรือคลิก เพื่อเลือกไฟล์'}
                                            placeholder={'รองรับเฉพาะไฟล์ pdf เท่านั้น'}
                                            component={UploadInput}
                                            maxFile={5}
                                            displayLabelFileList={false}
                                            allowExtension={['.pdf']}
                                            icon={<FileIcon className={classes.icon} />}
                                        />
                                    </Grid>
                                )}
                                {contentId !== 1 && (
                                    <Grid item xs={12}>
                                        <StyledDivHeaderFieldArray>
                                            <StyledTypoTitleFieldArray>{'ลิงก์ภายนอก'}</StyledTypoTitleFieldArray>
                                            {formData?.externalLink &&
                                                formData?.externalLink?.length <
                                                parseInt(`${process.env.REACT_APP_EXTERNAL_LINK_MAX}`) &&
                                                formData?.externalLink[formData?.externalLink?.length - 1].name &&
                                                formData?.externalLink[formData?.externalLink?.length - 1].url && (
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => {
                                                            form.mutators?.push('externalLink', { name: '', url: '' })
                                                        }}
                                                        classes={{ root: classes.addExternalLinkButton }}
                                                    >
                                                        <AddIcon />
                                                    </Button>
                                                )}
                                        </StyledDivHeaderFieldArray>
                                        <FieldArray name="externalLink">
                                            {({ fields }) =>
                                                fields.map((name, index) => (
                                                    <Grid
                                                        container
                                                        spacing={1}
                                                        key={`${name}_${index}`}
                                                        justifyContent="space-between"
                                                    >
                                                        <Grid item xs={5}>
                                                            <Field
                                                                name={`${name}.name`}
                                                                label={`ชื่อลิงก์ภายนอก`}
                                                                placeholder={`ชื่อลิงก์ภายนอก`}
                                                                component={Textbox}
                                                                fullWidth
                                                            />
                                                        </Grid>
                                                        <Grid
                                                            item
                                                            xs={
                                                                formData?.externalLink &&
                                                                    formData?.externalLink?.length > 1
                                                                    ? 6
                                                                    : 7
                                                            }
                                                        >
                                                            <Field
                                                                name={`${name}.url`}
                                                                label={`Link URL`}
                                                                placeholder={`Link URL`}
                                                                component={Textbox}
                                                                fullWidth
                                                            />
                                                        </Grid>
                                                        {formData?.externalLink &&
                                                            formData?.externalLink?.length > 1 ? (
                                                            <Grid item container xs={1} justifyContent="flex-end">
                                                                <Grid item>
                                                                    <Button
                                                                        variant="contained"
                                                                        color="error"
                                                                        onClick={() =>
                                                                            onClickDeleteFieldArrayItem(
                                                                                fields.remove,
                                                                                index,
                                                                                fields.value[index]?.name || 0,
                                                                            )
                                                                        }
                                                                        classes={{
                                                                            root: classes.deleteExternalLinkButton,
                                                                        }}
                                                                    >
                                                                        <MinusIcon />
                                                                    </Button>
                                                                </Grid>
                                                            </Grid>
                                                        ) : null}
                                                    </Grid>
                                                ))
                                            }
                                        </FieldArray>
                                    </Grid>
                                )}
                                <Grid item xs={12} className={classes.divButtonGroup}>
                                    <div className={classes.divButton}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => {
                                                onClickBack?.(formData, contentId)
                                            }}
                                            classes={{ root: classes.button }}
                                        >
                                            {t('BUTTON.CANCEL')}
                                        </Button>
                                    </div>
                                    <div className={classes.divButton}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            classes={{ root: classes.button }}
                                        >
                                            {t('BUTTON.SAVE')}
                                        </Button>
                                    </div>
                                </Grid>
                            </Grid>
                        </StyledDivForm>
                    </form>
                )}
            </FinalForm >
            <Dialog
                type="confirm"
                title={t('ALERT.TITLE.NOTIFY')}
                content={t('ALERT.CONTENT.DISCARD_CHANGE')}
                isOpen={openDialogBack ? true : false}
                onClickCancel={onClickConfirmBack}
                onClickConfirm={onClickCancelBack}
                labelButtonCancel={t('BUTTON.LEAVE')}
                labelButtonConfirm={t('BUTTON.STAY')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="confirm"
                title={t('ALERT.TITLE.NOTIFY')}
                content={'ยืนยันบันทึกข้อมูล'}
                isOpen={openDialogSave}
                onClickConfirm={() => {
                    onClickConfirmSave(contentId)
                }}
                onClickCancel={onClickCancelSave}
                labelButtonCancel={t('BUTTON.CANCEL')}
                labelButtonConfirm={t('BUTTON.CONFIRM')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="ok"
                title={t('ALERT.TITLE.NOTIFY')}
                content={alertMessageOk}
                isOpen={openDialogOk}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
            <Dialog
                type="ok"
                severity="error"
                title={t('ALERT.TITLE.ERROR')}
                content={alertMessageError}
                isOpen={openDialogError}
                onClickOk={onClickOk}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </>
    )
}

export default ManageContentForm

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    divButtonGroup: {
        display: 'flex',
        justifyContent: 'end',
        paddingBottom: '15px',
    },
    divButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    button: {
        width: '125px',
        minHeight: '37px',
        fontSize: '14px !important',
    },
    checkbox: {
        color: `${theme.palette.primary.dark} !important`,
        padding: '5px 10px !important',
    },
    checkboxChecked: {
        color: `${theme.palette.primary.dark} !important`,
        padding: '5px 10px !important',
    },
    icon: {
        fontSize: '70px !important',
        color: '#318AF14D',
        opacity: 30,
    },
    iconButton: {
        marginTop: '25px !important',
    },
    backButton: {
        padding: '0px !important',
        minWidth: '25px !important',
        minHeight: '25px !important',
        marginRight: '10px !important',
        color: `transparent !important`,
    },
    addExternalLinkButton: {
        minWidth: '25px !important',
        minHeight: '25px !important',
        padding: '0px !important',
    },
    deleteExternalLinkButton: {
        minWidth: '25px !important',
        minHeight: '25px !important',
        padding: '0px !important',
        marginTop: '30px !important',
    },
    editorWrapper: {
        minWidth: 0,
        maxWidth: "100%",
        overflowX: "hidden", 
        wordBreak: "break-word",
        "& .rdw-editor-wrapper, & .rdw-editor-main, & .DraftEditor-root, & .DraftEditor-editorContainer": {
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
        },

        "& img": { maxWidth: "100%", height: "auto" },
    },
}))

const StyledDivHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingBottom: '15px',
}))

const StyledTypoHeader = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}))

const StyledDivForm = styled('div')(({ theme }) => ({
    width: '100%',
    padding: '15px 30px',
    marginBottom: '15px',
    borderRadius: '5px',
    backgroundColor: theme.palette.text.secondary,
    boxShadow: theme.palette.getContrastText('shadow-bottom-gray'),
}))

const StyledTypoTitleFieldArray = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}))

const StyledDivHeaderFieldArray = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}))

const IconBack = styled(BackIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}))
